export type ImageConstraints = {
  maxFileSize?: number
  maxWidth?: number
  maxHeight?: number
  maxMegapixels?: number
}

type ValidationResult = {
  isValid: boolean
  errors: string[]
}

const mb = (bytes: number) => bytes / (1024 * 1024)

const readImageDimensions = (
  file: File,
): Promise<{ width: number; height: number } | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight }
      URL.revokeObjectURL(url)
      resolve(dims)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })

export const useImageValidation = () => {
  const validateImage = async (
    file: File,
    constraints: ImageConstraints,
  ): Promise<ValidationResult> => {
    const errors: string[] = []

    // Only images carry pixel dimensions; leave other file types to their own
    // (type/required) validation so this stays a drop-in for the shared uploader.
    if (!file.type?.startsWith('image/')) {
      return { isValid: true, errors }
    }

    if (constraints.maxFileSize && file.size > constraints.maxFileSize) {
      errors.push(
        `File is ${mb(file.size).toFixed(1)} MB — the maximum is ${mb(
          constraints.maxFileSize,
        ).toFixed(0)} MB. Please compress or export a smaller image.`,
      )
    }

    const dims = await readImageDimensions(file)
    if (!dims) {
      errors.push(
        'Unable to read the image — it may be corrupt or unsupported.',
      )
      return { isValid: false, errors }
    }

    const { width, height } = dims

    if (constraints.maxWidth && width > constraints.maxWidth) {
      errors.push(
        `Image width ${width}px exceeds the maximum ${constraints.maxWidth}px.`,
      )
    }
    if (constraints.maxHeight && height > constraints.maxHeight) {
      errors.push(
        `Image height ${height}px exceeds the maximum ${constraints.maxHeight}px.`,
      )
    }
    if (constraints.maxMegapixels) {
      const megapixels = (width * height) / 1_000_000
      if (megapixels > constraints.maxMegapixels) {
        errors.push(
          `Image resolution ${width}×${height}px (${megapixels.toFixed(
            1,
          )} MP) is too large and will slow down the storefront. The maximum is ${
            constraints.maxMegapixels
          } MP — please resize the image before uploading.`,
        )
      }
    }

    return { isValid: errors.length === 0, errors }
  }

  return { validateImage }
}

export const TEASER_IMAGE_CONSTRAINTS: ImageConstraints = {
  maxFileSize: 10 * 1024 * 1024,
  maxMegapixels: 24,
}
